// What the Trails page's blocks read, worked out once from the site's
// trail groups (SiteData::trailGroups): the same rows the Trails list
// block and the hub draw, so a trail cannot be open on one and closed
// on the other.
//
// Blocks: Trail directory, Types of trail, How we rate difficulty and
// Features and challenges. Each can sit on any page; the built-in
// Trails page is simply the one they start on.
import { DIFFICULTY_KEYS, difficultyLabel } from '@/lib/trailLabels.js';
import { ALL_FEATURES, TRAIL_TYPES } from '@/lib/trailCatalog.js';
import { iconSetForUse, resolveDifficultyColor } from '@/lib/trailIconSet.js';
import { isTrailOpen, trailsStatusOf } from '@/lib/trailsStatus.js';
import { lengthText } from './siteLength.js';

/** A site's groups, "Other trails" (network_id null) included. */
export function trailGroupsOf(siteData) {
    return Array.isArray(siteData?.trail_groups) ? siteData.trail_groups : [];
}

export function allTrailsOf(siteData) {
    return trailGroupsOf(siteData).flatMap((g) => g.trails || []);
}

/** "4 NETWORKS · 8 TRAILS · 23.9 MILES" — real networks only, and a
 *  distance only when some trail has one. */
export function trailTotalsLine(siteData) {
    const groups = trailGroupsOf(siteData);
    const nets = groups.filter((g) => g.network_id !== null).length;
    const trails = allTrailsOf(siteData);
    // The total is the home page's Total distance figure, read off the
    // same stats rather than added up again here: two sums of one
    // network printed 37 on one page and 36.3 on the other.
    const s = siteData?.stats || {};
    const metric = s.unit_system ? s.unit_system !== 'imperial' : trails.some((t) => t.distance?.unit === 'km');
    let total = Number(metric ? s.distance_km : s.distance_miles);
    if (!Number.isFinite(total) || !s.unit_system) {
        total = Math.round(trails.reduce((sum, t) => sum + (Number(t.distance?.value) || 0), 0));
    }
    const parts = [];
    if (nets) parts.push(`${nets} ${nets === 1 ? 'network' : 'networks'}`);
    parts.push(`${trails.length} ${trails.length === 1 ? 'trail' : 'trails'}`);
    if (total > 0) parts.push(`${total.toLocaleString('en-US')} ${metric ? 'km' : total === 1 ? 'mile' : 'miles'}`);
    return parts.join(' · ');
}

export const STATUS = {
    open: { tone: 'open', label: 'Open' },
    caution: { tone: 'caution', label: 'Caution' },
    closed: { tone: 'closed', label: 'Closed' },
};

/** One reading of "how is this trail", shared with the Trails list
 *  (lib/trailsStatus.js): maintenance counts as closed. Null when the
 *  organization keeps trail status off its public pages. */
export function statusOf(trail, siteData) {
    if (siteData?.show_status === false || !trail?.condition) return null;
    const s = trailsStatusOf([trail.condition]).status;
    if (s === 'closed') return STATUS.closed;
    if (s === 'caution' || s === 'partial') return STATUS.caution;
    return STATUS.open;
}

/** "2 trails · 7.7 mi · 1 open" for one network. */
export function groupStatsLine(group, siteData) {
    const trails = group?.trails || [];
    let total = 0;
    let unit = '';
    for (const t of trails) {
        if (t.distance?.value) { total += Number(t.distance.value); unit = t.distance.unit; }
    }
    const parts = [`${trails.length} ${trails.length === 1 ? 'trail' : 'trails'}`];
    if (total > 0) parts.push(`${total.toFixed(1)} ${unit === 'km' ? 'km' : 'mi'}`);
    if (siteData?.show_status !== false && trails.some((t) => t.condition)) {
        parts.push(`${trails.filter((t) => isTrailOpen(t.condition)).length} open`);
    }
    return parts.join(' · ');
}

/** The trail's difficulty symbol in the organization's own colors —
 *  the same one the Trails list and the trail's page draw. */
export function difficultyIconOf(trail, org) {
    if (!DIFFICULTY_KEYS.includes(trail?.difficulty)) return null;
    const set = iconSetForUse(trail.primary_use);
    return { difficulty: trail.difficulty, set, color: resolveDifficultyColor(org?.difficulty_colors, set, trail.difficulty) };
}

export function distanceText(trail) {
    const d = trail?.distance;
    return d ? lengthText(d.value, d.unit) : '';
}

/** "Moderate · Mountain biking, Hiking" */
export function trailMetaLine(trail) {
    return [difficultyLabel(trail?.difficulty), (trail?.uses || []).join(', ')].filter(Boolean).join(' · ');
}

/** A trail's page on the site. Show.vue adds the /site/{org} prefix on
 *  a preview URL, so this stays the vanity-domain shape. */
export function trailHref(trail) {
    return trail?.slug ? `/trails/${trail.slug}` : null;
}

/** A network's own page on the site (SiteNetwork); null for "Other
 *  trails", which is not a network. */
export function networkHref(group) {
    return group?.network_id != null && group?.network_slug ? `/networks/${group.network_slug}` : null;
}

/** Where a row of the Trail conditions block goes: a trail's page, or
 *  a network's. A network has a page only while the organization has
 *  trails switched on (SiteTrailsPage::available), which the payload
 *  says as site.network_pages; a link to a page that 404s is worse
 *  than a row that is not a link. */
export function conditionRowHref(siteData, mode, row) {
    if (mode === 'trails') return trailHref(row);
    if (siteData?.site?.network_pages === false) return null;
    return row?.slug ? `/networks/${row.slug}` : null;
}

/** How much of a network's description the directory shows before
 *  sending the reader to the network's page for the rest. */
export const DESCRIPTION_LIMIT = 220;
export const DESCRIPTION_LINES = 4;

/** The description cut at a word, or null when it fits whole. */
export function shortDescription(text, limit = DESCRIPTION_LIMIT, lines = DESCRIPTION_LINES) {
    const s = String(text || '').trim();
    if (!s) return null;
    const rows = s.split('\n');
    let cut = s;
    if (rows.length > lines) cut = rows.slice(0, lines).join('\n');
    if (cut.length > limit) {
        const at = cut.lastIndexOf(' ', limit);
        cut = cut.slice(0, at > limit * 0.6 ? at : limit);
    }
    if (cut === s) return null;
    return cut.replace(/[\s.,;:–—-]+$/u, '') + '…';
}

// ── Types of trail ──────────────────────────────────────────────
/** Types in catalog order that at least one public trail has, each
 *  with the networks it is found in. */
export function trailTypesIn(siteData) {
    const at = {};
    const links = {};
    for (const g of trailGroupsOf(siteData)) {
        for (const t of g.trails || []) {
            for (const key of t.trail_types || []) {
                (at[key] ||= new Set()).add(g.network_id === null ? null : g.network_name);
                if (g.network_id !== null) (links[key] ||= new Map()).set(g.network_name, networkHref(g));
            }
        }
    }
    return TRAIL_TYPES.filter((ty) => at[ty.key]).map((ty) => ({
        ...ty,
        networks: [...at[ty.key]].filter(Boolean),
        // The same networks with their pages, for the pills to link.
        network_links: [...(links[ty.key] || new Map())].map(([name, href]) => ({ name, href })),
    }));
}

// ── How we rate difficulty ──────────────────────────────────────
export const DIFFICULTY_DESCRIPTIONS = {
    easy: 'Gentle grades and a smooth, wide tread with no obstacles you cannot go around.',
    moderate: 'Steeper grades, with roots, rocks and short steep pitches. Some exposure.',
    hard: 'Sustained climbs over 10%, rock gardens, drops and tight switchbacks.',
    expert: 'Very steep, technical terrain with large features you cannot avoid.',
    pro: 'For experienced riders only: large jumps and drops with no ride-arounds.',
    access: 'Built for adaptive equipment and mobility devices.',
};

/** Levels in rating order that at least one public trail has, with a
 *  count and up to `examples` trail names to try. */
export function difficultyLevelsIn(siteData, org, examples = 2) {
    const trails = allTrailsOf(siteData);
    return DIFFICULTY_KEYS.map((key) => {
        const on = trails.filter((t) => t.difficulty === key);
        if (!on.length) return null;
        return {
            key,
            label: difficultyLabel(key),
            count: on.length,
            icon: difficultyIconOf(on[0], org),
            examples: on.slice(0, examples).map((t) => ({ name: t.name, href: trailHref(t) })),
        };
    }).filter(Boolean);
}

// ── Features and challenges ─────────────────────────────────────
/** Lots when at least half a network's trails have it, Some when any
 *  do, nothing otherwise. */
export function featureLevel(count, of) {
    if (!count || !of) return 0;
    return count * 2 >= of ? 2 : 1;
}

/** Rows of features found on at least one public trail, columns of the
 *  networks (and "Other trails" when it has any). */
export function featureMatrix(siteData) {
    const groups = trailGroupsOf(siteData).filter((g) => (g.trails || []).length);
    const columns = groups.map((g) => ({ key: String(g.network_id ?? 'none'), name: g.network_name }));
    const rows = ALL_FEATURES.map((f) => {
        const cells = groups.map((g) => {
            const n = (g.trails || []).filter((t) => (t.features || []).includes(f.key)).length;
            return featureLevel(n, g.trails.length);
        });
        return cells.some(Boolean) ? { ...f, cells } : null;
    }).filter(Boolean);
    return { columns, rows };
}

/** A block's own wording for a catalog entry, else the default. */
export function described(config, key, fallback) {
    const own = config?.descriptions?.[key];
    return typeof own === 'string' && own.trim() ? own : fallback;
}

/** The block's own color choices, under the names every block reads. */
export function blockStyle(config) {
    const s = config?.style || {};
    const v = {};
    if (s.bg) v['--block-bg'] = s.bg;
    if (s.text) v['--block-text'] = s.text;
    if (s.title || s.heading) v['--text-heading'] = s.title || s.heading;
    if (s.subtitle) v['--text-sub'] = s.subtitle;
    if (s.accent) v['--tp-accent'] = s.accent;
    return v;
}
